import React, { useRef, useState } from 'react';
import moment from 'moment';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/auth/clerkShim';
import type { Block } from '@staty/shared/schemas/block';
import { BlockView } from '@staty/shared/components/BlockRenderer';
import { extractCover, cleanBody } from '@staty/shared/utils/postContent';
import { hasBlocks } from '@staty/shared/utils/blockUtils';
import useEditorStore, { EntityRef, PostDraft, PostEntity } from './editorStore';
import BlockList from './BlockList';
import { uploadImage, UploadError } from './uploadImage';

/**
 * The canvas replica of the public post card (apps/app SinglePost.tsx) with
 * editing mounted IN PLACE:
 * - markup and Tailwind classes mirror SinglePost so the card composes
 *   identically (cover crop, header paddings, hero treatment for the newest
 *   post);
 * - the title is a transparent input styled as the public h2 (Oswald — the
 *   public app applies its display face to headings via element selectors);
 * - the body is an editable BlockList: every block type in place, drag
 *   reorder, "+" inserters — all rendering through the SAME shared pieces
 *   the public site uses.
 *
 * If this card ever drifts from SinglePost.tsx the editor is lying again —
 * change both or neither (ADM-07-18/19 pin the public side).
 */

interface Props {
  post: PostEntity;
  featured: boolean;
  selected: boolean;
  leagueName: string;
  onSelect: () => void;
}

// Matches apps/app global.css's display-face stack for h1-h4; the admin has
// no element-level Oswald rule, so the replica pins it inline.
const DISPLAY_FONT = "'Oswald', 'Arial Narrow', sans-serif";

export default function PostCardFrame({ post, featured, selected, leagueName, onSelect }: Props) {
  const ref: EntityRef = { kind: 'post', id: post._id };
  const draft = useEditorStore((s) => s.drafts[`post:${post._id}`]) as PostDraft | undefined;
  const patchDraft = useEditorStore((s) => s.patchDraft);

  // Unopened posts render read-only via the shared renderer path the public
  // site uses; clicking selects (and drafts) them.
  const title = draft ? draft.title : post.title || '';
  const showTitle = (draft ? draft.showTitle : post.showTitle !== false) && !!(title || draft);
  const showDate = draft ? draft.showDate : post.showDate !== false;
  const dateValue = draft
    ? draft.publishedAt
    : post.publishedAt
      ? new Date(post.publishedAt).toISOString().slice(0, 10)
      : null;
  const dateLabel = dateValue ? moment(dateValue).format('MMMM D, YYYY') : '';
  // Unopened legacy posts must show the cover the PUBLIC card shows — the
  // render-time lead-image extraction — not the raw body.
  const legacyExtract =
    !draft && !hasBlocks(post) ? extractCover(post.html || '') : null;
  const coverSrc = draft
    ? draft.coverImage?.src
    : post.coverImage?.src || legacyExtract?.coverSrc || undefined;
  const isDraftStatus = post.status !== 'published';

  return (
    <article
      className={`post-card canvas-card mb-8 bg-white border rounded-xl shadow-sm overflow-hidden ${
        selected ? 'canvas-card--selected' : 'border-gray-200'
      }`}
      onClick={onSelect}
      data-post-id={post._id}
    >
      {isDraftStatus && <span className="canvas-card__status">Draft</span>}
      {post.pinned === true && (
        <span className={`canvas-card__status canvas-card__status--pinned ${isDraftStatus ? 'canvas-card__status--second' : ''}`}>Pinned</span>
      )}
      <CoverSlot
        postRef={ref}
        coverSrc={coverSrc}
        title={title}
        featured={featured}
        editable={Boolean(draft)}
        cardSelected={selected}
        leagueName={leagueName}
      />
      {(showTitle || (showDate && dateLabel)) && (
        <div className={featured ? 'px-6 pt-6' : 'px-5 pt-5'}>
          {showTitle && (
            <input
              type="text"
              value={title}
              placeholder="Post title"
              onChange={(e) => draft && patchDraft(ref, { title: e.target.value })}
              readOnly={!draft}
              className={`canvas-title-input font-bold text-gray-900 leading-tight w-full ${
                featured ? 'text-3xl md:text-4xl' : 'text-2xl'
              }`}
              style={{ fontFamily: DISPLAY_FONT }}
              aria-label="Post title"
            />
          )}
          {showDate && dateLabel && (
            <p className={`${showTitle ? 'mt-2 ' : ''}text-xs font-semibold tracking-wide uppercase text-gray-500`}>
              {dateLabel}
            </p>
          )}
        </div>
      )}
      <div className="post-body">
        {draft ? (
          <BlockList entity={ref} blocks={draft.blocks} leagueName={leagueName} />
        ) : (
          <ReadOnlyBody post={post} legacyBodyHtml={legacyExtract?.bodyHtml} />
        )}
      </div>
    </article>
  );
}

interface CoverSlotProps {
  postRef: EntityRef;
  coverSrc: string | undefined;
  title: string;
  featured: boolean;
  editable: boolean;
  cardSelected: boolean;
  leagueName: string;
}

function CoverSlot({ postRef, coverSrc, title, featured, editable, cardSelected, leagueName }: CoverSlotProps) {
  const { getToken } = useAuth();
  const patchDraft = useEditorStore((s) => s.patchDraft);
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File | undefined) => {
    if (!file || uploading) return;
    setUploading(true);
    try {
      const src = await uploadImage(file, leagueName, getToken);
      patchDraft(postRef, { coverImage: { src, alt: title || '' } });
    } catch (err) {
      toast.error(err instanceof UploadError ? err.message : 'Failed to upload cover');
    } finally {
      setUploading(false);
    }
  };

  if (!coverSrc) {
    // Affordance only while the card is being edited — an unopened card must
    // look exactly like the public one.
    if (!editable || !cardSelected) return null;
    return (
      <>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <button
          type="button"
          className="canvas-cover-add"
          disabled={uploading}
          onClick={(e) => {
            e.stopPropagation();
            fileInput.current?.click();
          }}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          {uploading ? 'Uploading…' : 'Add cover image'}
        </button>
      </>
    );
  }

  return (
    <div className={`post-cover block canvas-cover ${featured ? 'post-cover--featured' : ''}`}>
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <img src={coverSrc} alt={title} />
      {editable && (
        <div className="canvas-cover__actions" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileInput.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Replace'}
          </button>
          <button
            type="button"
            aria-label="Remove cover"
            onClick={() => patchDraft(postRef, { coverImage: null })}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

function ReadOnlyBody({ post, legacyBodyHtml }: { post: PostEntity; legacyBodyHtml?: string }) {
  if (hasBlocks(post)) {
    return (
      <>
        {(post.blocks as Block[]).map((block) => (
          <BlockView key={block.id} block={block} />
        ))}
      </>
    );
  }
  // Same transforms as the public SinglePost legacy path.
  return (
    <div
      className="ql-editor"
      dangerouslySetInnerHTML={{ __html: cleanBody(legacyBodyHtml ?? post.html ?? '') }}
    />
  );
}
